import deleteIcon from "../../../assets/svg-icons/deleteRed.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import attachIcon from "../../../assets/svg-icons/attach.svg";
import back from "../../../assets/svg-icons/back.svg";
import Input from "../../../components/input";
import Select from "../../../components/select";
import { useState, useEffect } from "react";
import {
  getSingleSecurityGroup,
  listServers,
  createSecurityGroupRule,
  deleteSecurityGroupRule,
  createSecurityGroupInstance,
  removeSecurityGroupInstance,
  listSecurityGroups,
} from "../../../hooks/local/userReducer";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormik, FieldArray } from "formik";
import * as Yup from "yup";
import { showSuccessMessage, showErrorMessage } from "../../../hooks/constants";

const ManageSecurityGroups = () => {


  const { id } = useParams();
  const dispatch = useDispatch();

  const selectedProjectId = useSelector(
    (state) => state.user.selectedProject.id,
  );

  const [groupDetails, setGroupDetails] = useState({
    id: "",
    name: "",
    description: "",
  });
  const [rules, setRules] = useState([]);
  const [instanceOptions, setInstanceOptions] = useState([]);
  const [securityGroupOptions, setSecurityGroupOptions] = useState([]);
  const [remoteType, setRemoteType] = useState("CIDR");
  const [deletingRuleId, setDeletingRuleId] = useState("");
  const [confirmDeleteRuleId, setConfirmDeleteRuleId] = useState("");
  const [isConfirmRuleModalOpen, setIsConfirmRuleModalOpen] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const [isDetaching, setIsDetaching] = useState(false);

  const openConfirmRuleModal = (ruleId) => {
    setConfirmDeleteRuleId(ruleId);
    setIsConfirmRuleModalOpen(true);
  };

  const closeConfirmRuleModal = () => {
    setIsConfirmRuleModalOpen(false);
    setConfirmDeleteRuleId("");
  };

  const fetchServers = async () => {
    try {
      const { payload } = await dispatch(listServers(selectedProjectId));
      const options = payload.map((server) => ({
        value: server.id,
        label: server.name,
      }));
      setInstanceOptions(options);
    } catch (error) {
      console.error("Error fetching servers:", error);
    }
  };

  const fetchSecurityGroups = async () => {
    try {
      const { payload } = await dispatch(listSecurityGroups(selectedProjectId));
      const options = payload.map((sg) => ({
        value: sg.id,
        label: sg.name,
      }));
      setSecurityGroupOptions([
        { value: "", label: "Select security group" },
        ...options,
      ]);
    } catch (error) {
      console.error("Error fetching security groups:", error);
    }
  };

  useEffect(() => {
    if (selectedProjectId) {
      fetchServers();
      fetchSecurityGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  const [instance, setInstance] = useState("");

  const handleInstanceChange = (event) => {
    setInstance(event.target.value);
  };

  const fetchSecurityGroupRuleData = async () => {
    const { payload } = await dispatch(
      getSingleSecurityGroup({
        projectId: selectedProjectId,
        securityGroupId: id,
      }),
    );

    // Extract data from response (handles both direct data and Axios response)
    const responseData = payload?.data || payload;
    const data = responseData?.security_group || responseData?.data?.security_group;

    if (data) {
      setGroupDetails({
        id: data?.id,
        name: data?.name,
        description: data?.description,
      });
    }

    console.log("Security Group Data:", data); // Debugging line

    setRules(
      data?.security_group_rules?.map((rule) => ({
        id: rule.id,
        description: rule.description || "No description",
        direction: rule.direction,
        ethertype: rule.ethertype,
        ipProtocol: rule.protocol || "Any",
        portRange: rule.port_range_min
          ? `${rule.port_range_min}-${rule.port_range_max}`
          : "Any",
        remoteType: rule.remote_type || "CIDR",
        remoteIpPrefix: rule.remote_ip_prefix || "",
        remoteSecurityGroupId: rule.remote_group_name === "Unknown" ? "-" : rule.remote_group_name === null ? "-" : rule.remote_group_name || "",
      })) || [],
    );
  };

  useEffect(() => {
    fetchSecurityGroupRuleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedProjectId]);

  useEffect(() => {
    if (id) fetchSecurityGroupRuleData();
  }, [id]);

  const [addRuleModal, setAddRuleModal] = useState(false);

  const toggleAddRuleModal = () => {
    setAddRuleModal(!addRuleModal);
    if (addRuleModal) {
      setSelectedType("");
      setRemoteType("CIDR");
    }
  };

  const [instanceModal, setInstanceModal] = useState(false);

  const toggleInstanceModal = () => {
    if (!isAttaching) {
      setInstanceModal(!instanceModal);
      if (instanceModal) {
        setInstance("");
      }
    }
  };

  const [detachInstanceModal, setDetachInstanceModal] = useState(false);

  const toggleDetachInstanceModal = () => {
    if (!isDetaching) {
      setDetachInstanceModal(!detachInstanceModal);
      if (detachInstanceModal) {
        setDetachInstance("");
      }
    }
  };

  const [detachInstance, setDetachInstance] = useState("");

  const handleDetachInstanceChange = (event) => {
    setDetachInstance(event.target.value);
  };

  const securityGroupType = [
    { name: "SSH", code: "22" },
    { name: "HTTP", code: "80" },
    { name: "HTTPS", code: "443" },
    { name: "MYSQL", code: "3306" },
    { name: "All TCP", code: "tcp" },
    { name: "All UDP", code: "udp" },
    { name: "ICMP", code: "icmp" },
    { name: "Custom TCP", code: "custom" },
  ];

  const directionOptions = [
    { label: "Ingress", value: "ingress" },
    { label: "Egress", value: "egress" },
  ];

  const [selectedType, setSelectedType] = useState("");

  const handleTypeChange = (event) => {
    const selectedCode = event.target.value;
    setSelectedType(selectedCode);
    switch (selectedCode) {
      case "22":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "22",
          port_range_max: "22",
        }));
        break;
      case "80":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "80",
          port_range_max: "80",
        }));
        break;
      case "443":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "443",
          port_range_max: "443",
        }));
        break;
      case "3306":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "3306",
          port_range_max: "3306",
        }));
        break;
      case "tcp":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "",
          port_range_max: "",
        }));
        break;
      case "udp":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "",
          port_range_max: "",
        }));
        break;
      case "icmp":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "",
          port_range_max: "",
        }));
        break;
      case "custom":
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "",
          port_range_max: "",
        }));
        break;
      default:
        createNewSecurityGroupRule.setValues((prev) => ({
          ...prev,
          port_range_min: "",
          port_range_max: "",
        }));
        break;
    }
  };

  const handleRemoteChange = (event) => {
    const newRemoteType = event.target.value;

    setRemoteType(newRemoteType);
    // Auto-set remote IP prefix when switching to CIDR for egress rules
    let defaultIpPrefix = "";
    if (newRemoteType === "CIDR") {
      if (createNewSecurityGroupRule.values.ethertype === "IPv6" &&
        createNewSecurityGroupRule.values.direction === "egress") {
        defaultIpPrefix = "::/0";
      } else if (createNewSecurityGroupRule.values.ethertype === "IPv4" &&
        createNewSecurityGroupRule.values.direction === "egress") {
        defaultIpPrefix = "0.0.0.0/0";
      }
    }

    createNewSecurityGroupRule.setValues((prev) => ({
      ...prev,
      remoteType: newRemoteType,
      remoteIpPrefix: defaultIpPrefix,
      remoteSecurityGroupId: "",
    }));
  };

  const createAttachSecurityGroupInstance = useFormik({
    initialValues: {},
    onSubmit: async (values, { resetForm }) => {
      if (!instance) {
        showErrorMessage("Please select an instance");
        return;
      }

      setIsAttaching(true);
      try {
        let instanceData = {
          ...values,
          server_id: instance,
          security_group_id: id,
        };

        const { payload } = await dispatch(
          createSecurityGroupInstance({
            instanceData,
            projectId: selectedProjectId,
          }),
        );

        // Extract data from response (handles both direct data and Axios response)
        const responseData = payload?.data || payload;
        
        // Check success: statuscode "00" OR HTTP status 200/201/204
        const isSuccess =
          responseData?.statuscode === "00" ||
          payload?.status === 200 ||
          payload?.status === 201 ||
          payload?.status === 204;

        if (isSuccess) {
          resetForm();
          setInstance("");
          toggleInstanceModal();
          showSuccessMessage(responseData?.statusmessage || "Security group attached to instance successfully");
        } else {
          // Show only the specific error message from the API response
          // This prevents duplicate error messages (generic + specific)
          const errorMessage = responseData?.statusmessage || responseData?.message;
          if (errorMessage) {
            showErrorMessage(errorMessage);
          }
        }
      } catch (error) {
        // Only show error if it hasn't been handled already
        // Extract specific error message from the error object
        const errorMessage = error?.response?.data?.statusmessage || error?.response?.data?.message;
        if (errorMessage) {
          showErrorMessage(errorMessage);
        } else {
          console.error("Unexpected error attaching security group:", error);
        }
      } finally {
        setIsAttaching(false);
      }
    },
  });

  const removeSecurityGroupFromInstance = useFormik({
    initialValues: {},
    onSubmit: async (values, { resetForm }) => {
      if (!detachInstance) {
        showErrorMessage("Please select an instance");
        return;
      }

      setIsDetaching(true);
      try {
        const instanceData = {
          security_group_id: id,
          server_id: detachInstance,
        };

        const { payload } = await dispatch(
          removeSecurityGroupInstance({
            instanceData,
            projectId: selectedProjectId,
          }),
        );

        // Extract data from response (handles both direct data and Axios response)
        const responseData = payload?.data || payload;
        
        // Check success: statuscode "00" OR HTTP status 200/201/204
        const isSuccess =
          responseData?.statuscode === "00" ||
          payload?.status === 200 ||
          payload?.status === 201 ||
          payload?.status === 204;

        if (isSuccess) {
          resetForm();
          setDetachInstance("");
          toggleDetachInstanceModal();
          showSuccessMessage(responseData?.statusmessage || "Security group detached from instance successfully");
        } else {
          // Show only the specific error message from the API response
          // This prevents duplicate error messages (generic + specific)
          const errorMessage = responseData?.statusmessage || responseData?.message;
          if (errorMessage) {
            showErrorMessage(errorMessage);
          } else {
            showErrorMessage("Failed to detach security group from instance");
          }
        }
      } catch (error) {
        // Only show error if it hasn't been handled already
        // Extract specific error message from the error object
        const errorMessage = error?.response?.data?.statusmessage || error?.response?.data?.message;
        if (errorMessage) {
          showErrorMessage(errorMessage);
        } else {
          console.error("Unexpected error detaching security group:", error);
        }
      } finally {
        setIsDetaching(false);
      }
    },
  });

  const createNewSecurityGroupRule = useFormik({
    initialValues: {
      ethertype: "IPv4",
      direction: "ingress",
      port_range_max: "",
      port_range_min: "",
      description: "",
      remoteType: "CIDR",
      remoteIpPrefix: "0.0.0.0/0",
      remoteSecurityGroupId: "",
    },
    validationSchema: Yup.object({
      ethertype: Yup.string().required("Ether type cannot be empty"),
      direction: Yup.string().required("Direction cannot be empty"),
      port_range_max: Yup.string(),
      port_range_min: Yup.string(),
      description: Yup.string(),
      remoteIpPrefix: Yup.string().when([], {
        is: () => remoteType === "CIDR",
        then: (schema) => schema.required("CIDR is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      remoteSecurityGroupId: Yup.string().when([], {
        is: () => remoteType === "Security Group",
        then: (schema) => schema.required("Security Group is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      const protocol = ["22", "80", "443", "3306", "custom"].includes(
        selectedType,
      )
        ? "tcp"
        : selectedType === "tcp"
          ? "tcp"
          : selectedType === "udp"
            ? "udp"
            : selectedType === "icmp"
              ? "icmp"
              : "";

      let dataToSubmit = {
        ...values,
        direction: values.direction,
        protocol,
        remote_type: values.remoteType,
        remote_ip_prefix: values.remoteType === "CIDR" ? values.remoteIpPrefix : undefined,
        remote_group_id: values.remoteType === "Security Group" ? values.remoteSecurityGroupId : undefined,
      };

      if (["tcp", "udp", "icmp"].includes(selectedType)) {
        delete dataToSubmit.port_range_min;
        delete dataToSubmit.port_range_max;
      }

      if (dataToSubmit.description === "") {
        delete dataToSubmit.description;
      }

      delete dataToSubmit.remoteType;

      const { payload } = await dispatch(
        createSecurityGroupRule({
          securityGroupRuleData: dataToSubmit,
          projectId: selectedProjectId,
          securityGroupId: groupDetails?.id,
        }),
      );

      if (payload) {
        toggleAddRuleModal();
        fetchSecurityGroupRuleData();
        resetForm();
        setSelectedType("");
        setRemoteType("CIDR");
      }
    },
  });

  const handleDeleteSecurityGroupRule = async (securityGroupRuleId) => {
    setDeletingRuleId(securityGroupRuleId);
    try {
      const { payload } = await dispatch(
        deleteSecurityGroupRule({
          projectId: selectedProjectId,
          securityGroupRuleId,
        })
      );
      // Extract data from response (handles both direct data and Axios response)
      const responseData = payload?.data || payload;

      // Check success: statuscode "00" OR HTTP status 200/204/201 (successful deletion responses)
      const isSuccess =
        responseData?.statuscode === "00" ||
        payload?.status === 200 ||
        payload?.status === 204 ||
        payload?.status === 201;

      if (isSuccess) {
        setRules((prevRules) => prevRules.filter((rule) => rule.id !== securityGroupRuleId));
        showSuccessMessage(responseData?.statusmessage || "Security group rule deleted successfully");
        fetchSecurityGroupRuleData();
        closeConfirmRuleModal();
      } else {
        // Only show error message if there's an actual error message
        const errorMsg = responseData?.statusmessage || responseData?.message;
        if (errorMsg) {
          showErrorMessage(errorMsg);
        }
      }
    } catch (error) {
      showErrorMessage("Failed to delete security group rule");
    } finally {
      setDeletingRuleId("");
    }
  };



  return (
    <div className="px-4 md:px-10 py-6 min-h-screen relative">
      <div className="mb-6 grid gap-1">
        <div className="flex gap-3 items-center">
          <div
            className="p-2 cursor-pointer bg-primaryBlue/10 rounded-md"
            onClick={() => window.history.go(-1)}
          >
            <img alt="" src={back} className="h-4" />
          </div>
          <p className="crimson-font text-xl font-medium text-primaryBlue">
            Manage{" "}
            <span className="underline font-semibold">{groupDetails.name}</span>
          </p>
        </div>
        <p className="text-sm text-black/50 dark:text-white/50 truncate gap-1 flex">
          <span className="">Group ID:</span>
          <span className="truncate font-semibold">{groupDetails.id}</span>
        </p>
        <p className="text-sm text-black/50 dark:text-white/50 truncate gap-1 flex">
          <span className="">Description:</span>
          <span className="truncate font-semibold">
            {groupDetails.description}
          </span>
        </p>
      </div>

      <div className="mb-6 md:flex md:justify-between md:items-center grid gap-4">
        <div className="flex items-center gap-3">
          <div
            onClick={toggleInstanceModal}
            className="px-4 py-3 text-sm md:text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer flex items-center gap-1"
          >
            <img src={attachIcon} alt="Attach" className="h-4" />
            <span>Attach to instance</span>
          </div>
          <div
            onClick={toggleDetachInstanceModal}
            className="px-4 py-3 text-sm md:text-xs border-[1.5px] border-red-500 rounded-md text-red-500 cursor-pointer flex items-center gap-1"
          >
            <img src={deleteIcon} alt="Detach" className="h-4" />
            <span>Detach from instance</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm md:text-xs">
          <div>
            <div
              onClick={toggleAddRuleModal}
              className="px-4 py-3 border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer"
            >
              Add new rule
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432] dark:text-white">
        <table className="w-full text-sm text-left overflow-hidden">
          <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
            <tr>
              <th scope="col" className="whitespace-nowrap py-4 px-6"></th>
              <th scope="col" className="whitespace-nowrap py-4 px-6">
                Direction
              </th>
              <th scope="col" className="whitespace-nowrap py-4 px-6">
                Ether Type
              </th>
              <th scope="col" className="whitespace-nowrap py-4 px-6">
                IP Protocol
              </th>
              <th scope="col" className="whitespace-nowrap py-4 px-6">
                Port Range
              </th>
              <th scope="col" className="whitespace-nowrap py-4 px-6">
                Remote IP Prefix
              </th>
              <th scope="col" className="whitespace-nowrap py-4 px-6">
                Remote Security Group
              </th>
              <th scope="col" className="whitespace-nowrap py-4 px-6">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/50 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
            {rules.length > 0 ? (
              rules.map((row, index) => (
                <tr
                  key={index}
                  className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                >
                  <td className="text-start py-4 px-6">{index + 1}</td>
                  <td className="text-start py-4 px-6">{row.direction || "Any"}</td>
                  <td className="text-start py-4 px-6">{row.ethertype}</td>
                  <td className="text-start py-4 px-6">{row.ipProtocol}</td>
                  <td className="text-start py-4 px-6">{row.portRange}</td>
                  <td className="text-start py-4 px-6 max-w-20 truncate">
                    {row.remoteIpPrefix || "-"}
                  </td>
                  <td className="text-start py-4 px-6 max-w-20 truncate">
                    {row.remoteSecurityGroupId || "-"}
                  </td>
                  <td className="text-start py-4 px-6">
                    <div
                      className="bg-red-300/10 hover:bg-red-500/10 rounded-md cursor-pointer transition-all p-2 w-fit"
                      onClick={() => {
                        if (!deletingRuleId) openConfirmRuleModal(row.id);
                      }}
                      style={{ opacity: deletingRuleId === row.id ? 0.5 : 1, pointerEvents: deletingRuleId ? "none" : "auto" }}
                    >
                      {deletingRuleId === row.id ? (
                        <div className="loader h-4 w-4" />
                      ) : (
                        <img src={deleteIcon} alt="" className="h-4" />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-4">
                  No rules available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {addRuleModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Add a new rule</p>
              <span onClick={toggleAddRuleModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <div>
              <div className="mt-6 mb-4 text-sm">Security group rules:</div>
              <div className="mb-4 grid grid-cols-1 gap-4 items-end">
                <div className="grid gap-1">
                  <span className="text-sm">Rule</span>
                  <select
                    className="py-4 rounded-md border border-[#c4c4c460] focus:outline-0 bg-white dark:bg-slate-800 text-sm"
                    value={selectedType}
                    onChange={handleTypeChange}
                  >
                    <option value="" disabled>
                      Select Rule
                    </option>
                    {securityGroupType.map((securityType, index) => (
                      <option key={index} value={securityType.code}>
                        {securityType.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-1">
                  <span className="text-sm">Direction</span>
                  <Select
                    name="direction"
                    className="py-4 rounded-md border border-[#c4c4c460] focus:outline-0 bg-white dark:bg-slate-800 text-sm"
                    options={directionOptions}
                    value={createNewSecurityGroupRule.values.direction}
                    onChange={(e) => {
                      const newDirection = e.target.value;
                      createNewSecurityGroupRule.setFieldValue("direction", newDirection);
                      // Auto-set remote IP prefix for IPv6 egress rules
                      if (newDirection === "egress" &&
                        createNewSecurityGroupRule.values.ethertype === "IPv6" &&
                        remoteType === "CIDR") {
                        createNewSecurityGroupRule.setFieldValue("remoteIpPrefix", "::/0");
                      } else if (newDirection === "egress" &&
                        createNewSecurityGroupRule.values.ethertype === "IPv4" &&
                        remoteType === "CIDR") {
                        createNewSecurityGroupRule.setFieldValue("remoteIpPrefix", "0.0.0.0/0");
                      }
                    }}
                    onError={
                      createNewSecurityGroupRule.errors.direction &&
                        createNewSecurityGroupRule.touched.direction
                        ? createNewSecurityGroupRule.errors.direction
                        : null
                    }
                  />
                </div>

                <div className="grid gap-1">
                  <span className="text-sm">EtherType</span>
                  <Select
                    name="ethertype"
                    className="py-4 rounded-md border border-[#c4c4c460] focus:outline-0 bg-white dark:bg-slate-800 text-sm"
                    options={[
                      { value: "IPv4", label: "IPv4" },
                      { value: "IPv6", label: "IPv6" },
                    ]}
                    value={createNewSecurityGroupRule.values.ethertype}
                    onChange={(e) => {
                      const newEthertype = e.target.value;
                      createNewSecurityGroupRule.setFieldValue("ethertype", newEthertype);
                      // Auto-set remote IP prefix for IPv6 egress rules
                      if (newEthertype === "IPv6" &&
                        createNewSecurityGroupRule.values.direction === "egress" &&
                        remoteType === "CIDR") {
                        createNewSecurityGroupRule.setFieldValue("remoteIpPrefix", "::/0");
                      } else if (newEthertype === "IPv4" &&
                        createNewSecurityGroupRule.values.direction === "egress" &&
                        remoteType === "CIDR") {
                        createNewSecurityGroupRule.setFieldValue("remoteIpPrefix", "0.0.0.0/0");
                      }
                    }}
                    onError={
                      createNewSecurityGroupRule.errors.ethertype &&
                        createNewSecurityGroupRule.touched.ethertype
                        ? createNewSecurityGroupRule.errors.ethertype
                        : null
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Port range (min)"
                    name="port_range_min"
                    value={createNewSecurityGroupRule.values.port_range_min}
                    onChange={createNewSecurityGroupRule.handleChange}
                    onBlur={createNewSecurityGroupRule.handleBlur}
                    onError={
                      createNewSecurityGroupRule.touched.port_range_min &&
                        createNewSecurityGroupRule.errors.port_range_min
                        ? createNewSecurityGroupRule.errors.port_range_min
                        : null
                    }
                    disabled={selectedType !== "custom"}
                  />
                  <Input
                    label="Port range (max)"
                    name="port_range_max"
                    value={createNewSecurityGroupRule.values.port_range_max}
                    onChange={createNewSecurityGroupRule.handleChange}
                    onBlur={createNewSecurityGroupRule.handleBlur}
                    onError={
                      createNewSecurityGroupRule.touched.port_range_max &&
                        createNewSecurityGroupRule.errors.port_range_max
                        ? createNewSecurityGroupRule.errors.port_range_max
                        : null
                    }
                    disabled={selectedType !== "custom"}
                  />
                </div>
                <div className="grid gap-1">
                  <span className="text-sm">Remote</span>
                  <select
                    className="py-4 rounded-md border border-[#c4c4c460] focus:outline-0 bg-white dark:bg-slate-800 text-sm"
                    value={remoteType}
                    onChange={handleRemoteChange}
                  >
                    <option value="CIDR">CIDR</option>
                    <option value="Security Group">Security Group</option>
                  </select>
                </div>
                {remoteType === "CIDR" ? (
                  <div className="grid gap-1">
                    <Input
                      label="CIDR"
                      name="remoteIpPrefix"
                      value={createNewSecurityGroupRule.values.remoteIpPrefix}
                      onChange={createNewSecurityGroupRule.handleChange}
                      onBlur={createNewSecurityGroupRule.handleBlur}
                      onError={
                        createNewSecurityGroupRule.touched.remoteIpPrefix &&
                          createNewSecurityGroupRule.errors.remoteIpPrefix
                          ? createNewSecurityGroupRule.errors.remoteIpPrefix
                          : null
                      }
                      placeholder="e.g., 192.32.12.50/29"
                    />
                  </div>
                ) : (
                  <div className="grid gap-1">
                    <span className="text-sm">Security Group</span>
                    <Select
                      name="remoteSecurityGroupId"
                      className="py-4 rounded-md border border-[#c4c4c460] focus:outline-0 bg-white dark:bg-slate-800 text-sm"
                      options={securityGroupOptions}
                      value={createNewSecurityGroupRule.values.remoteSecurityGroupId}
                      onChange={(e) => {
                        createNewSecurityGroupRule.setFieldValue("remoteSecurityGroupId", e.target.value);
                        createNewSecurityGroupRule.setFieldTouched("remoteSecurityGroupId", true);
                      }}
                      onError={
                        createNewSecurityGroupRule.touched.remoteSecurityGroupId &&
                          createNewSecurityGroupRule.errors.remoteSecurityGroupId &&
                          !createNewSecurityGroupRule.values.remoteSecurityGroupId
                          ? createNewSecurityGroupRule.errors.remoteSecurityGroupId
                          : null
                      }
                    />
                  </div>
                )}
                <div>
                  <Input
                    type="text"
                    label="Description"
                    placeholder="Start typing..."
                    name="description"
                    value={createNewSecurityGroupRule.values.description}
                    onChange={createNewSecurityGroupRule.handleChange}
                    onBlur={createNewSecurityGroupRule.handleBlur}
                    onError={
                      createNewSecurityGroupRule.touched.description &&
                        createNewSecurityGroupRule.errors.description
                        ? createNewSecurityGroupRule.errors.description
                        : null
                    }
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  console.log("Form Values:", createNewSecurityGroupRule.values);
                  console.log("Form Errors:", createNewSecurityGroupRule.errors);
                  console.log("Form Touched:", createNewSecurityGroupRule.touched);
                  createNewSecurityGroupRule.handleSubmit(e);
                }}
                className="text-sm md:text-xs text-white bg-[#1DA1F2] rounded px-8 py-4 cursor-pointer"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {instanceModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">
                Attach security group to instance
              </p>
              <span
                onClick={toggleInstanceModal}
                className={`cursor-pointer ${isAttaching ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ pointerEvents: isAttaching ? 'none' : 'auto' }}
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>

            <div>
              <select
                className="py-4 rounded-md border border-[#c4c4c460] focus:outline-0 bg-white dark:bg-slate-800 text-sm w-full"
                value={instance}
                onChange={handleInstanceChange}
                disabled={isAttaching}
              >
                <option value="" disabled>
                  Select Instance
                </option>
                {instanceOptions.map((instance, index) => (
                  <option key={index} value={instance.value}>
                    {instance.label}
                  </option>
                ))}
              </select>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    createAttachSecurityGroupInstance.handleSubmit(e);
                  }}
                  disabled={isAttaching || !instance}
                  className="bg-[#1DA1F2] text-white text-sm px-6 py-4 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isAttaching ? (
                    <>
                      <div className="loader h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Attaching...</span>
                    </>
                  ) : (
                    "Attach to instance"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detachInstanceModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3 max-h-[80%] overflow-y-scroll">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">
                Detach security group from instance
              </p>
              <span
                onClick={toggleDetachInstanceModal}
                className={`cursor-pointer ${isDetaching ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ pointerEvents: isDetaching ? 'none' : 'auto' }}
              >
                <img alt="" src={close} className="h-5" />
              </span>
            </div>

            <div>
              <select
                className="py-4 rounded-md border border-[#c4c4c460] focus:outline-0 bg-white dark:bg-slate-800 text-sm w-full disabled:opacity-50 disabled:cursor-not-allowed"
                value={detachInstance}
                onChange={handleDetachInstanceChange}
                disabled={isDetaching}
              >
                <option value="" disabled>
                  Select Instance
                </option>
                {instanceOptions.map((instance, index) => (
                  <option key={index} value={instance.value}>
                    {instance.label}
                  </option>
                ))}
              </select>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    removeSecurityGroupFromInstance.handleSubmit(e);
                  }}
                  disabled={isDetaching || !detachInstance}
                  className="bg-red-600 text-white text-sm px-6 py-4 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDetaching ? (
                    <>
                      <div className="loader h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Detaching...</span>
                    </>
                  ) : (
                    "Detach from instance"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isConfirmRuleModalOpen && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-8 w-full md:w-1/3">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Delete rule</p>
              <span onClick={closeConfirmRuleModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <p className="text-sm mb-6 dark:text-white/80">Are you sure you want to delete this security group rule? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="border rounded px-4 py-2 text-sm"
                onClick={closeConfirmRuleModal}
                disabled={deletingRuleId === confirmDeleteRuleId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-600 text-white rounded px-4 py-2 text-sm"
                onClick={() => handleDeleteSecurityGroupRule(confirmDeleteRuleId)}
                disabled={deletingRuleId === confirmDeleteRuleId}
              >
                {deletingRuleId === confirmDeleteRuleId ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSecurityGroups;